import styles from '../FJCommon.module.less';
import FJHeader from '@src/components/FJModals/FJShareModal/FJShareContent/FJHeader.tsx';
import YoutubeIcon from '../../../../../assets/youtube.svg?react';
import CopyIcon from '../../../../../assets/copy.svg?react';
import { type SOCIAL_ITEM, SOCIAL_KEY } from '@src/constants/FJShareSocialConstant.ts';
import FJLocalStore from '@src/store/FJLocalStore';
import { useEffect, useRef, useState } from 'react';
import FJExpansionInput from '@src/components/FJModals/FJShareModal/FJShareContent/FJExpansionInput.tsx';
import FJAIGenerateButton from '@src/components/FJModals/FJShareModal/FJShareContent/FJAIGenerateButton.tsx';
import FJCheckBoxGroup from '@src/components/FJModals/FJShareModal/FJShareContent/FJCheckBoxGroup.tsx';
import FJPublishManager from '@src/manager/publish/FJPublishManager.ts';
import FJAITextLoading from '@src/components/FJModals/FJShareModal/FJShareContent/FJAITextLoading.tsx';
import DefaultAvatarIcon from '../../../../../assets/profile.svg?react';
import FJUtil from '@src/util/FJUtil';
import FJMessageStore, { MESSAGE_TYPE } from '@src/store/FJMessageStore';
import FJErrorTitle from '@src/components/FJModals/FJShareModal/FJShareContent/FJErrorTitle.tsx';
import FJEvent from '@src/store/FJEvent';
import FJToolTip from '@src/common/components/FJToolTip/FJToolTip';

interface Props {
    data: SOCIAL_ITEM;
    onCreateDescription?: () => void;
    onCreateTitle?: () => void;
    defaultTitle?: string;
    defaultDescription?: string;
    defaultVisibility?: string;
    isLoadingText?: boolean;
    isLoadingDescription?: boolean;
}

const FJYoutubeView = ({
    data,
    onCreateDescription,
    onCreateTitle,
    defaultTitle,
    defaultDescription,
    defaultVisibility,
    isLoadingText,
    isLoadingDescription,
}: Props) => {
    const inputTitleRef = useRef(null);
    const inputDescRef = useRef(null);

    const [visibility, setVisibility] = useState('public');
    const [isShowTitleCopy, setIsShowTitleCopy] = useState(true);
    const [isShowDescriptionCopy, setIsShowDescriptionCopy] = useState(true);

    const [isLackOfTitle, setIsLackOfTitle] = useState(false);
    const [isLackOfDescription, setIsLackOfDescription] = useState(false);

    useEffect(() => {
        if (defaultVisibility) {
            setVisibility(defaultVisibility);
        }
    }, [defaultVisibility]);
    useEffect(() => {
        setIsShowTitleCopy(!!defaultTitle);
    }, [defaultTitle]);
    useEffect(() => {
        setIsShowDescriptionCopy(!!defaultDescription);
    }, [defaultDescription]);

    useEffect(() => {
        FJEvent.subscribe('check options', () => {
            const target = FJPublishManager.getPublishTarget(SOCIAL_KEY.YOUTUBE);
            if (target) {
                if (!target.title.trim()) {
                    setIsLackOfTitle(true);
                }
                if (!target.description.trim()) {
                    setIsLackOfDescription(true);
                }
            }
        });
    }, []);

    const _copyTitle = () => {
        const text = inputTitleRef.current.getValue();
        if (text) {
            FJUtil.copyTextToClipBoard(text);
            FJMessageStore.addMessage(FJLocalStore._('copy-successful'), MESSAGE_TYPE.SUCCESS);
        }
    };
    const _copyDescription = () => {
        const text = inputDescRef.current.getValue();
        if (text) {
            FJUtil.copyTextToClipBoard(text);
            FJMessageStore.addMessage(FJLocalStore._('copy-successful'), MESSAGE_TYPE.SUCCESS);
        }
    };

    const _renderTitleGenerateButton = () => {
        return (
            <div className={styles.input_foot}>
                <FJAIGenerateButton onClick={onCreateTitle} />
                {isShowTitleCopy && (
                    <FJToolTip title={FJLocalStore._('copy')}>
                        <div className={styles.copy_btn} onClick={_copyTitle}>
                            <CopyIcon />
                        </div>
                    </FJToolTip>
                )}
            </div>
        );
    };

    const _renderDescriptionGenerateButton = () => {
        return (
            <div className={styles.input_foot}>
                <FJAIGenerateButton onClick={onCreateDescription} />
                {isShowDescriptionCopy && (
                    <FJToolTip title={FJLocalStore._('copy')}>
                        <div className={styles.copy_btn} onClick={_copyDescription}>
                            <CopyIcon />
                        </div>
                    </FJToolTip>
                )}
            </div>
        );
    };

    const _setVisibility = (value: string) => {
        const target = FJPublishManager.getPublishTarget(SOCIAL_KEY.YOUTUBE);
        if (target) {
            target.privacyState = value;
        }
        setVisibility(value);
    };

    const _titleChange = (value: string) => {
        const target = FJPublishManager.getPublishTarget(SOCIAL_KEY.YOUTUBE);
        if (target) {
            target.title = value;
            target.titleChanged = true;
        }
        setIsLackOfTitle(false);
        setIsShowTitleCopy(!!value);
    };

    const _descriptionChange = (value: string) => {
        const target = FJPublishManager.getPublishTarget(SOCIAL_KEY.YOUTUBE);
        if (target) {
            target.description = value;
            target.descriptionChanged = true;
        }
        setIsLackOfDescription(false);
        setIsShowDescriptionCopy(!!value);
    };

    const { avatar, email } = data;
    return (
        <div className={styles.item}>
            <FJHeader
                avatarUrl={avatar}
                email={email}
                icon={<YoutubeIcon />}
                platform={SOCIAL_KEY.YOUTUBE}
                defaultAvatar={<DefaultAvatarIcon />}
            />
            <p className={styles.t5} style={{ marginTop: 15, marginBottom: 10 }}>
                {FJLocalStore._('title')}
            </p>
            <div style={{ width: '100%', height: 102, position: 'relative' }}>
                <FJExpansionInput
                    ref={inputTitleRef}
                    initValue={isLoadingText ? '' : defaultTitle}
                    footHeight={40}
                    maxLength={100}
                    placeholder={FJLocalStore._('add-video-title')}
                    renderExpansion={_renderTitleGenerateButton}
                    onChange={_titleChange}
                />
                {isLackOfTitle && <FJErrorTitle />}
                {isLoadingText && <FJAITextLoading />}
            </div>

            <p className={styles.t5} style={{ marginTop: isLackOfTitle ? 25 : 15, marginBottom: 10 }}>
                {FJLocalStore._('description')}
            </p>
            <div style={{ width: '100%', height: 178, position: 'relative' }}>
                <FJExpansionInput
                    ref={inputDescRef}
                    initValue={isLoadingDescription ? '' : defaultDescription}
                    maxLength={1000}
                    footHeight={40}
                    placeholder={FJLocalStore._('add-video-description')}
                    renderExpansion={_renderDescriptionGenerateButton}
                    onChange={_descriptionChange}
                />
                {isLackOfDescription && <FJErrorTitle />}
                {isLoadingDescription && <FJAITextLoading />}
            </div>
            <div className={styles.border_box} style={{ marginTop: isLackOfDescription ? 30 : 20 }}>
                <p className={styles.t5}>{FJLocalStore._('Visibility')}</p>
                <FJCheckBoxGroup
                    targets={['public', 'unlisted', 'private']}
                    target={visibility}
                    onChange={_setVisibility}
                    checkBoxStyleType={'circle'}
                    disableReselect={true}
                />
            </div>
        </div>
    );
};

export default FJYoutubeView;
